import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import CustomSelect from "../../../../ui/components/custom-select/custom-select";
import DateInput from "../../../../ui/components/date-input/date-input";
import { ListModel } from "../../../models/list-model";
import { ProfesionalCreateModel } from "../../../models/profesional-create";
import { condicionFiscalServices } from "../../../services/condicion-fiscal.services";
import { profesionalServices } from "../../../services/profesional.services";
import { tituloServices } from "../../../services/titulo.services";
import { handle400Error } from "../../../utils/handle-400-errors";
import { localidades } from "../../../utils/localidades";
import {
  AREAS_OPTIONS,
  POBLACION_OPTIONS,
  REDES_CONFIG,
  RedKey,
  buildRedes,
  formatCuit,
} from "../../../utils/profesional-ficha.utils";
import ChipGroup from "../chip-group/chip-group";
import "./profesional-create.scss";

const PROVINCIA = "La Pampa";

const ROLES: ListModel[] = [
  { value: "PROFESIONAL", label: "Profesional" },
  { value: "PROFESIONALWEB", label: "Profesional + Portal Web" },
];

const schema = yup
  .object()
  .shape({
    nombre: yup
      .string()
      .required("Campo requerido")
      .matches(/^[^0-9]+$/, "No se permiten números"),
    apellido: yup
      .string()
      .required("Campo requerido")
      .matches(/^[^0-9]+$/, "No se permiten números"),
    dni: yup.string().required("Campo requerido"),
    cuit: yup
      .string()
      .required("Campo requerido")
      .matches(/^\d{2}-\d{8}-\d{1}$/, "Formato inválido (99-99999999-9)"),
    tituloId: yup.string().required("Campo requerido"),
    expedidoPor: yup.string().required("Campo requerido"),
    expedidoFecha: yup.date().nullable(),
    matriculaHab: yup.string(),
    matriculaHabFecha: yup.date().nullable(),
    matriculaProf: yup.string().required("Campo requerido"),
    matriculaProfFecha: yup.date().nullable(),
    domicilioReal: yup.string().required("Campo requerido"),
    localidadReal: yup.string().required("Campo requerido"),
    cpReal: yup.string().required("Campo requerido"),
    telefonoReal: yup.string(),
    domicilioLaboral: yup.string(),
    localidadLaboral: yup.string(),
    cpLaboral: yup.string(),
    telefonoLaboral: yup.string(),
    condicionFiscalId: yup.string().required("Campo requerido"),
    iibbTipo: yup.string(),
    iibbNumero: yup.string(),
    observacion: yup.string().max(500, "Máximo 500 caracteres"),
    userName: yup.string().required("Campo requerido"),
    email: yup
      .string()
      .email("Ingrese un email válido")
      .required("Campo requerido"),
    password: yup
      .string()
      .required("Campo requerido")
      .matches(
        /^(?=\w*\d)(?=\w*[A-Z])(?=\w*[a-z])\S{8,16}$/,
        "8 a 16 caracteres, sin espacios, con mayúscula, minúscula y número."
      ),
    confirmPassword: yup
      .string()
      .required("Campo requerido")
      .oneOf([yup.ref("password")], "Las contraseñas no coinciden"),
    role: yup.string().required("Campo requerido"),
  })
  .required();

export default function ProfesionalCreate() {
  const {
    register,
    getFieldState,
    formState,
    handleSubmit,
    formState: { errors },
    control,
    setError,
  } = useForm<any>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [titulos, setTitulos] = useState<ListModel[]>([]);
  const [condicionesFiscales, setCondicionesFiscales] = useState<
    ListModel[]
  >([]);
  const localidadOptions: ListModel[] = localidades.map((loc) => ({
    value: loc,
    label: loc,
  }));

  const [iibb, setIibb] = useState(false);
  const [atiendeEnConsultorio, setAtiendeEnConsultorio] = useState(false);
  const [atiendePorObraSocial, setAtiendePorObraSocial] = useState(false);
  const [areas, setAreas] = useState<string[]>([]);
  const [poblacion, setPoblacion] = useState<string[]>([]);
  const [redUrls, setRedUrls] = useState<Record<RedKey, string>>({
    facebook: "",
    instagram: "",
    linkedin: "",
    "x.com": "",
  });
  const setRedUrl = (key: RedKey, val: string) =>
    setRedUrls((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    tituloServices
      .lookup()
      .then((response) => {
        const items: ListModel[] = response.data.map((element: any) => ({
          value: element.id,
          label: element.value,
        }));
        setTitulos(items);
      })
      .catch(() => {});
    condicionFiscalServices
      .lookup()
      .then((response) => {
        const items: ListModel[] = response.data.map((element: any) => ({
          value: element.id,
          label: element.value,
        }));
        setCondicionesFiscales(items);
      })
      .catch(() => {});
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    setSaving(true);
    const payload: ProfesionalCreateModel = {
      ...data,
      cuit: data.cuit.replace(/\D/g, ""),
      provinciaReal: PROVINCIA,
      provinciaLaboral: PROVINCIA,
      iibb,
      atiendeEnConsultorio,
      atiendePorObraSocial,
      areas,
      poblacion,
      redes: buildRedes(redUrls),
    };
    profesionalServices
      .create(payload)
      .then(() => {
        navigate("/profesionales", {
          state: {
            textAlert: `El profesional ${data.nombre} ${data.apellido} fue creado correctamente.`,
          },
        });
      })
      .catch((error) => {
        if (error?.response?.status === 400) {
          handle400Error(error.response.data.errors, setError);
        }
      })
      .finally(() => setSaving(false));
  });

  const inputProps = { register, getFieldState, formState, errors };

  return (
    <div className="profesional-create-container">
      <Typography variant="h5" gutterBottom>
        Nuevo Profesional
      </Typography>
      <Box component="form" autoComplete="off" onSubmit={onSubmit}>
        <Typography variant="subtitle1" className="section-title">
          Datos personales
        </Typography>
        <div className="row">
          <CustomInput
            name="nombre"
            label="Nombre"
            type="text"
            required
            maxLength={50}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="apellido"
            label="Apellido"
            type="text"
            required
            maxLength={50}
            {...inputProps}
          ></CustomInput>
        </div>
        <div className="row">
          <CustomInput
            name="dni"
            label="DNI"
            type="text"
            required
            maxLength={20}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="cuit"
            label="CUIT"
            type="text"
            required
            maxLength={13}
            mask={formatCuit}
            placeholder="99-99999999-9"
            {...inputProps}
          ></CustomInput>
        </div>

        <Typography variant="subtitle1" className="section-title">
          Título y matrícula
        </Typography>
        <div className="row">
          <Controller
            name="tituloId"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="tituloId"
                label="Título"
                isMultiple={false}
                required
                options={titulos}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <CustomInput
            name="expedidoPor"
            label="Expedido por"
            type="text"
            required
            maxLength={100}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="expedidoFecha"
            control={control}
            render={({ field }) => (
              <DateInput
                field={field}
                name="expedidoFecha"
                label="Fecha de expedición"
                value={field.value}
                {...inputProps}
              ></DateInput>
            )}
          />
        </div>
        <div className="row">
          <CustomInput
            name="matriculaHab"
            label="Matrícula habilitante"
            type="text"
            maxLength={50}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="matriculaHabFecha"
            control={control}
            render={({ field }) => (
              <DateInput
                field={field}
                name="matriculaHabFecha"
                label="Fecha matrícula habilitante"
                value={field.value}
                {...inputProps}
              ></DateInput>
            )}
          />
        </div>
        <div className="row">
          <CustomInput
            name="matriculaProf"
            label="Matrícula profesional"
            type="text"
            required
            maxLength={50}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="matriculaProfFecha"
            control={control}
            render={({ field }) => (
              <DateInput
                field={field}
                name="matriculaProfFecha"
                label="Fecha matrícula profesional"
                value={field.value}
                {...inputProps}
              ></DateInput>
            )}
          />
        </div>

        <Typography variant="subtitle1" className="section-title">
          Domicilio real
        </Typography>
        <div className="row">
          <CustomInput
            name="domicilioReal"
            label="Domicilio"
            type="text"
            required
            maxLength={200}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="localidadReal"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="localidadReal"
                label="Localidad"
                isMultiple={false}
                required
                options={localidadOptions}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <CustomInput
            name="cpReal"
            label="Código postal"
            type="text"
            required
            maxLength={10}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="telefonoReal"
            label="Teléfono"
            type="text"
            maxLength={100}
            {...inputProps}
          ></CustomInput>
        </div>

        <Typography variant="subtitle1" className="section-title">
          Domicilio laboral
        </Typography>
        <div className="row">
          <CustomInput
            name="domicilioLaboral"
            label="Domicilio"
            type="text"
            maxLength={200}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="localidadLaboral"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="localidadLaboral"
                label="Localidad"
                isMultiple={false}
                options={localidadOptions}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <CustomInput
            name="cpLaboral"
            label="Código postal"
            type="text"
            maxLength={10}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="telefonoLaboral"
            label="Teléfono"
            type="text"
            maxLength={100}
            {...inputProps}
          ></CustomInput>
        </div>

        <Typography variant="subtitle1" className="section-title">
          Situación fiscal
        </Typography>
        <div className="row">
          <Controller
            name="condicionFiscalId"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="condicionFiscalId"
                label="Condición fiscal"
                isMultiple={false}
                required
                options={condicionesFiscales}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={iibb}
                onChange={(e) => setIibb(e.target.checked)}
              />
            }
            label="Inscripto en IIBB"
          />
        </div>
        {iibb && (
          <div className="row">
            <CustomInput
              name="iibbTipo"
              label="Tipo IIBB"
              type="text"
              maxLength={50}
              {...inputProps}
            ></CustomInput>
            <CustomInput
              name="iibbNumero"
              label="Número IIBB"
              type="text"
              maxLength={50}
              {...inputProps}
            ></CustomInput>
          </div>
        )}

        <Typography variant="subtitle1" className="section-title">
          Atención
        </Typography>
        <div className="row">
          <FormControlLabel
            control={
              <Checkbox
                checked={atiendeEnConsultorio}
                onChange={(e) => setAtiendeEnConsultorio(e.target.checked)}
              />
            }
            label="Atiende en consultorio"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={atiendePorObraSocial}
                onChange={(e) => setAtiendePorObraSocial(e.target.checked)}
              />
            }
            label="Atiende por obra social"
          />
        </div>
        <ChipGroup
          label="Áreas de especialización"
          options={AREAS_OPTIONS}
          selected={areas}
          onChange={setAreas}
        />
        <ChipGroup
          label="Población que atiende"
          options={POBLACION_OPTIONS}
          selected={poblacion}
          onChange={setPoblacion}
        />

        <Typography variant="subtitle1" className="section-title">
          Redes y contacto
        </Typography>
        <div className="row">
          {REDES_CONFIG.map(({ key, label, placeholder }) => (
            <TextField
              key={key}
              label={label}
              type="url"
              placeholder={placeholder}
              value={redUrls[key]}
              onChange={(e) => setRedUrl(key, e.target.value)}
              inputProps={{ maxLength: 300 }}
              fullWidth
            />
          ))}
        </div>

        <Typography variant="subtitle1" className="section-title">
          Observación
        </Typography>
        <div className="row">
          <TextField
            label="Observación"
            multiline
            rows={3}
            fullWidth
            error={!!errors.observacion}
            helperText={errors.observacion?.message?.toString()}
            inputProps={{ maxLength: 500 }}
            {...register("observacion")}
          />
        </div>

        <Typography variant="subtitle1" className="section-title">
          Credenciales de acceso
        </Typography>
        <div className="row">
          <CustomInput
            name="userName"
            label="Nombre de usuario"
            type="text"
            required
            maxLength={25}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="email"
            label="Email"
            type="text"
            required
            maxLength={100}
            {...inputProps}
          ></CustomInput>
        </div>
        <div className="row">
          <CustomInput
            name="password"
            label="Contraseña"
            type="password"
            required
            maxLength={50}
            assistiveText="8 a 16 caracteres, sin espacios, con mayúscula, minúscula y número."
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="confirmPassword"
            label="Repetir contraseña"
            type="password"
            required
            maxLength={50}
            {...inputProps}
          ></CustomInput>
        </div>
        <div className="row">
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="role"
                label="Rol"
                isMultiple={false}
                required
                options={ROLES}
                {...inputProps}
              ></CustomSelect>
            )}
          />
        </div>

        <div className="row actions">
          <Button variant="outlined" onClick={() => navigate("/profesionales")}>
            Cancelar
          </Button>
          <Button
            className="create-button"
            variant="contained"
            type="submit"
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </Box>
    </div>
  );
}
